import { BlockingPromiseQueue } from 'common-utils';

import {
  getEventCommonProperties,
  ProductDetails,
} from './common-event-properties';
import { removeUndefined } from './remove-undefined';
import { Events } from './tracking-events/event';
import { TrackingTool } from './tracking-tool';
import { TrackingProperties } from './use-tracking';

export class Tracking {
  private blockingQueue = new BlockingPromiseQueue();
  private readonly _tools: TrackingTool[];
  private readonly _states: { [toolName: string]: boolean };
  private pendingEvents: Array<{
    event: Events;
    properties?: TrackingProperties;
  }> = [];
  private _productDetails: ProductDetails;

  constructor(tools: TrackingTool[], productDetails: ProductDetails) {
    this._tools = tools;
    this._states = {};
    this._productDetails = productDetails;
    tools.forEach((tool) => (this._states[tool.getName()] = false));
  }

  startTracking = async (tools?: string[]) => {
    const actualTools = tools ?? this._tools.map((tool) => tool.getName());
    await this.blockingQueue.enqueue(async () => {
      const startPromises = actualTools.map(async (toolName) => {
        if (this._states[toolName]) {
          return;
        }
        await this._tools.find((tool) => tool.getName() === toolName)?.turnOn();
      });
      await Promise.allSettled(startPromises);
      actualTools.map((toolName) => (this._states[toolName] = true));
    });
    if (this.pendingEvents.length !== 0) {
      this.pendingEvents.map((eventData) => {
        this.trackCommonEvent(eventData.event, eventData.properties);
      });
      this.pendingEvents = [];
    }
  };

  stopTracking = async (tools?: string[]) => {
    const actualTools = tools ?? this._tools.map((tool) => tool.getName());
    await this.blockingQueue.enqueue(async () => {
      const startPromises = actualTools.map(async (toolName) => {
        if (!this._states[toolName]) {
          return;
        }
        this._states[toolName] = true;
        await this._tools
          .find((tool) => tool.getName() === toolName)
          ?.turnOff();
      });
      await Promise.allSettled(startPromises);
    });
  };

  private isActive = (trackingTool: TrackingTool) => {
    return this._states[trackingTool.getName()] === true;
  };

  private activeTools = () => {
    return this._tools.filter(this.isActive);
  };

  trackEvent = async (options: {
    category: string;
    action: string;
    label: string;
    customData?: Partial<TCustomData>;
  }) => {
    await this.blockingQueue.enqueue(async () => {
      const promises = this.activeTools().map(
        async (tool) => await tool.trackEvent?.(options),
      );
      await Promise.allSettled(promises);
    });
  };

  trackCommonEvent = async (event: Events, properties?: TrackingProperties) => {
    if (this.activeTools().length === 0) {
      this.pendingEvents.push({
        event,
        properties,
      });
    } else {
      const updatedProperties = removeUndefined({
        ...getEventCommonProperties(this._productDetails),
        ...properties,
      });
      await this.blockingQueue.enqueue(async () => {
        const promises = this.activeTools().map(
          async (tool) =>
            await tool.trackCommonEvent?.(
              removeUndefined(event),
              updatedProperties,
            ),
        );
        await Promise.allSettled(promises);
      });
    }
  };

  public specialEvents = {
    signIn: async (userDetails: TUserDetails) => {
      await this.blockingQueue.enqueue(async () => {
        const promises = this.activeTools().map(
          async (tool) => await tool.trackSpecialEvent?.signIn?.(userDetails),
        );
        await Promise.allSettled(promises);
      });
    },
    onUserCreated: async () => {
      await this.blockingQueue.enqueue(async () => {
        const promises = this.activeTools().map(
          async (tool) => await tool.trackSpecialEvent?.onUserCreated?.(),
        );
        await Promise.allSettled(promises);
      });
    },
    pageView: async (pageView: Partial<TPageView>) => {
      await this.blockingQueue.enqueue(async () => {
        const promises = this.activeTools().map(
          async (tool) => await tool.trackSpecialEvent?.pageView?.(pageView),
        );
        await Promise.allSettled(promises);
      });
    },
  };
}

type TUserDetails = {
  name: string;
  ssoId: number;
  email: string;
};

type TPageView = {
  productName: string;
  journalName: string;
  publisherName: string;
  flowType: string;
  anonymousOrSignedIn: string;
  pageURL: string;
  sourceURL: string;
  ip: string;
  pageTitle: string;
  utm_source: string | string[] | undefined;
  utm_medium: string | string[] | undefined;
  utm_campaign: string | string[] | undefined;
};
export type TCustomData = { [key: string]: string | number };
